/**
 * ContextMenu.tsx - Universal Context Menu Component
 * 
 * Right-click context menu with full OpCentral orchestrator integration
 * Migrated from Viewr with enhancements
 * 
 * FEATURES:
 * - Right-click context menus
 * - Keyboard navigation (arrows, enter, escape)
 * - Type-to-select functionality
 * - Submenu support
 * - Separator support
 * - Disabled/danger states
 * - Click outside to close
 * - Position calculation (stay on screen)
 * - Action logging (Actr)
 * - Skinnr/Stylr theming support
 * 
 * ARCHITECTURE:
 * - Uses @opcentral/actr for action logging
 * - Aligns with MenuItem interface from Menu system
 * 
 * CHANGE HISTORY:
 * - 251115_1820 [current] danielbeer: Migrated from Viewr with orchestrator integration
 * - 251101_0847 [viewr] danielbeer: Added TypeScript interfaces
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { ContextMenuProps, ContextMenuItem } from './types';

export const ContextMenu: React.FC<ContextMenuProps> = ({ 
  isOpen, 
  position, 
  items = [], 
  onClose,
  onItemClick,
  theme = 'dark',
  className = ''
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [typeBuffer, setTypeBuffer] = useState('');
  const typeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter out separators and hidden items for navigation
  const navigableItems = items.filter(item => 
    item.type !== 'separator' && !item.disabled && !item.hidden
  );

  // Reset state when menu opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
      setOpenSubmenu(null);
      setTypeBuffer('');
      
      actr?.execute('contextmenu:open', {
        itemCount: items.length,
        position
      });
    } else {
      actr?.execute('contextmenu:close', {});
    }
  }, [isOpen, items.length, position, actr]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default for navigation keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }

      switch (e.key) {
        case 'ArrowDown':
          setSelectedIndex(prev => {
            const next = prev + 1;
            return next >= navigableItems.length ? 0 : next;
          });
          break;

        case 'ArrowUp':
          setSelectedIndex(prev => {
            const next = prev - 1;
            return next < 0 ? navigableItems.length - 1 : next;
          });
          break;

        case 'ArrowRight':
          // Open submenu if current item has one
          if (navigableItems[selectedIndex]?.submenu) {
            setOpenSubmenu(navigableItems[selectedIndex].id);
            actr?.execute('contextmenu:submenu-open', {
              itemId: navigableItems[selectedIndex].id,
              label: navigableItems[selectedIndex].label
            });
          }
          break;

        case 'ArrowLeft':
          // Close submenu
          if (openSubmenu) {
            actr?.execute('contextmenu:submenu-close', {
              itemId: openSubmenu
            });
          }
          setOpenSubmenu(null);
          break;

        case 'Enter':
          // Execute selected item
          const selectedItem = navigableItems[selectedIndex];
          if (selectedItem && !selectedItem.disabled) {
            if (selectedItem.submenu) {
              setOpenSubmenu(selectedItem.id);
              actr?.execute('contextmenu:submenu-open', {
                itemId: selectedItem.id,
                label: selectedItem.label
              });
            } else {
              handleItemAction(selectedItem);
            }
          }
          break;

        case 'Escape':
          if (openSubmenu) {
            setOpenSubmenu(null);
            actr?.execute('contextmenu:submenu-close', {
              itemId: openSubmenu
            });
          } else {
            onClose?.();
          }
          break;

        default:
          // Type-to-select
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            handleTypeToSelect(e.key);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, selectedIndex, navigableItems, openSubmenu, onClose, actr]);

  // Type-to-select functionality
  const handleTypeToSelect = useCallback((char: string) => {
    // Clear previous timeout
    if (typeTimeoutRef.current) {
      clearTimeout(typeTimeoutRef.current);
    }

    // Add character to buffer
    const newBuffer = typeBuffer + char.toLowerCase();
    setTypeBuffer(newBuffer);

    // Find matching item
    const matchIndex = navigableItems.findIndex(item => 
      item.label?.toLowerCase().startsWith(newBuffer)
    );

    if (matchIndex !== -1) {
      setSelectedIndex(matchIndex);
      actr?.execute('contextmenu:type-select', {
        buffer: newBuffer,
        itemId: navigableItems[matchIndex].id,
        label: navigableItems[matchIndex].label
      });
    }

    // Clear buffer after 1 second
    typeTimeoutRef.current = setTimeout(() => {
      setTypeBuffer('');
    }, 1000);
  }, [typeBuffer, navigableItems, actr]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };

    // Use capture phase to prevent background clicks
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('contextmenu', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('contextmenu', handleClickOutside, true);
    };
  }, [isOpen, onClose]);

  // Handle item action
  const handleItemAction = useCallback((item: ContextMenuItem) => {
    actr?.execute('contextmenu:item-click', {
      itemId: item.id,
      label: item.label,
      type: item.type
    });

    if (item.action) {
      item.action();
    }
    
    if (item.href) {
      window.open(item.href, item.target || '_self');
    }

    onItemClick?.(item);
    onClose?.();
  }, [actr, onItemClick, onClose]);

  // Handle item click
  const handleItemClick = useCallback((item: ContextMenuItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (item.disabled) return;

    if (item.type === 'submenu' && item.submenu) {
      const newSubmenuState = openSubmenu === item.id ? null : item.id;
      setOpenSubmenu(newSubmenuState);
      
      if (newSubmenuState) {
        actr?.execute('contextmenu:submenu-open', {
          itemId: item.id,
          label: item.label
        });
      } else {
        actr?.execute('contextmenu:submenu-close', {
          itemId: item.id
        });
      }
    } else {
      handleItemAction(item);
    }
  }, [openSubmenu, handleItemAction, actr]);

  // Handle mouse enter for selection
  const handleMouseEnter = useCallback((index: number) => {
    const navigableIndex = navigableItems.findIndex(navItem => navItem.id === items[index].id);
    if (navigableIndex !== -1) {
      setSelectedIndex(navigableIndex);
    }
  }, [navigableItems, items]);

  // Render menu items
  const renderItems = (menuItems: ContextMenuItem[], isSubmenu = false) => {
    return menuItems.filter(item => !item.hidden).map((item: ContextMenuItem, index: number) => {
      // Separator
      if (item.type === 'separator') {
        return <div key={`separator-${index}`} className="context-menu__separator" role="separator" />;
      }

      // Custom render
      if (item.render) {
        return (
          <div key={item.id || index} className="context-menu__custom">
            {item.render(item)}
          </div>
        );
      }

      const isSelected = !isSubmenu && navigableItems[selectedIndex]?.id === item.id;
      const hasSubmenu = item.type === 'submenu' && item.submenu && item.submenu.length > 0;
      const isSubmenuOpen = openSubmenu === item.id;

      return (
        <div
          key={item.id || index}
          className={`context-menu__item ${isSelected ? 'context-menu__item--selected' : ''} ${item.disabled ? 'context-menu__item--disabled' : ''} ${item.danger ? 'context-menu__item--danger' : ''}`}
          onClick={(e) => handleItemClick(item, e)}
          onMouseEnter={() => handleMouseEnter(index)}
          role="menuitem"
          aria-disabled={item.disabled}
          aria-label={item.label}
        >
          {item.icon && (
            <span className="context-menu__item-icon" aria-hidden="true">
              {item.icon}
            </span>
          )}
          <span className="context-menu__item-label">{item.label}</span>
          {item.shortcut && (
            <span className="context-menu__item-shortcut" aria-label={`Keyboard shortcut: ${item.shortcut}`}>
              {item.shortcut}
            </span>
          )}
          {hasSubmenu && (
            <span className="context-menu__item-arrow" aria-hidden="true">▶</span>
          )}
          
          {hasSubmenu && isSubmenuOpen && item.submenu && (
            <div className="context-menu__submenu" role="menu">
              {renderItems(item.submenu, true)}
            </div>
          )}
        </div>
      );
    });
  };

  if (!isOpen || !position) return null;

  // Calculate position (ensure menu stays on screen)
  const menuStyle: React.CSSProperties = {
    left: position.x,
    top: position.y,
  };

  const menuClasses = [
    'context-menu',
    `context-menu--${theme}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={menuRef}
      className={menuClasses}
      style={menuStyle}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      role="menu"
      aria-label="Context menu"
    >
      {renderItems(items)}
    </div>
  );
};

ContextMenu.displayName = 'ContextMenu';
